/**
 * inventoryFilterAxes — single source of truth for what filter axes exist
 * and how they work.
 *
 * Each axis is a self-contained definition: how to enumerate its values
 * from the document set, how to match a doc against a value, and how to
 * render the value as a chip label. New axes are added here (and nowhere
 * else) — the reducer, applyFilters, picker, and chip rendering all read
 * from this registry.
 *
 * Shape:
 *   {
 *     key:           stable identifier used in the filters[] array
 *     label:         human label shown as the chip prefix ("Purpose: Motion")
 *     icon:          emoji prefix for chips and picker rows
 *     visibleWhen:   (ctx) => bool — controls whether this axis appears in
 *                    the picker at all (e.g. Checklist only when one exists)
 *     getValues:     (docs, ctx) => [{ value, label, count?, group? }]
 *                    enumeration for the picker. `group` is an optional
 *                    visual hint (parenthesized) — purely cosmetic.
 *     matches:       (doc, value) => bool — predicate used by applyFilters
 *     formatValue:   (value, ctx) => string — pretty-print a value for the
 *                    chip body, when getValues isn't the right place
 *                    (e.g. chip rendering after a page reload)
 *   }
 */

import { getCanonicalType, getTypeLabel } from '../hooks/useInventoryData';
import { docNoteCount, docMineNoteCount } from './notesIndex';
import { isCutChild } from '../components/segments/cutChild';

// ── Static option lists (replicated from the old InventoryToolbar) ─────

const STATUS_OPTIONS = [
  { value: 'completed', label: '✅ Ready' },
  { value: 'processing', label: '⏳ Processing' },
  { value: 'pending', label: '🕐 Pending' },
  { value: 'failed', label: '❌ Failed' },
];

const TIER_OPTIONS = [
  { value: 'skim', label: '⬜ Skim only' },
  { value: 'surface', label: '🔍 Surface search' },
  { value: 'deep', label: '🔎 Deep search' },
  { value: 'indexing', label: '⏳ Upgrading…' },
];

const LABEL_STATUS_OPTIONS = [
  { value: 'auto_classified', label: 'Auto-classified' },
  { value: 'corrected', label: 'Manually corrected' },
  { value: 'unclassified', label: 'Unclassified' },
];

// Where a row comes from (plan §3.4c): a document cut out of a file, a file as it was
// uploaded, or — in the Files unit — a file that holds documents inside.
const ORIGIN_OPTIONS = [
  { value: 'cut', label: 'Cut out of a file' },
  { value: 'uploaded', label: 'Uploaded as a file' },
  { value: 'holds', label: 'Holds documents inside' },
];

const CHECKLIST_OPTIONS = [
  { value: 'matched', label: '✓ Matched to checklist' },
  { value: 'not_matched', label: '☐ Not matched' },
];

// ── Helpers ────────────────────────────────────────────────────────────

function humanize(s) {
  if (!s) return '';
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function findOption(list, value) {
  return list.find(o => o.value === value);
}

// Cut files by id → file name, for the "Source file" axis: a chip is rendered from its
// value alone (the file's id — names hold commas, which the ?f= codec splits on), and
// the file's own row is hidden in the Documents unit. Published by useInventoryData
// from the full list, the way notesIndex is.
let _sourceFileNames = {};

export function setSourceFileNames(documents) {
  const names = {};
  for (const d of documents || []) {
    if (d.is_compilation_container) names[d.id] = d.filename;
  }
  _sourceFileNames = names;
}

const sourceFileName = (id) => _sourceFileNames[id] || 'a file';

// ── Axis registry ──────────────────────────────────────────────────────

export const FILTER_AXES = {
  purpose: {
    key: 'purpose',
    label: 'Purpose',
    icon: '🏷️',
    getValues: (docs) => {
      const counts = {};
      const groups = {};
      for (const d of docs) {
        const p = d.document_purpose;
        if (!p || p === 'unknown') continue;
        counts[p] = (counts[p] || 0) + 1;
        if (!groups[p]) groups[p] = d.document_purpose_group || d.document_category || '';
      }
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([value, count]) => ({
          value,
          label: humanize(value),
          count,
          group: groups[value] || undefined,
        }));
    },
    matches: (d, v) => d.document_purpose === v,
    formatValue: (v) => humanize(v),
  },

  type: {
    key: 'type',
    label: 'File Type',
    icon: '📎',
    getValues: (docs) => {
      const counts = {};
      for (const d of docs) {
        const t = getCanonicalType(d);
        counts[t] = (counts[t] || 0) + 1;
      }
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([value, count]) => ({
          value,
          label: getTypeLabel(value) || value,
          count,
        }));
    },
    matches: (d, v) => getCanonicalType(d) === v,
    formatValue: (v) => getTypeLabel(v) || v,
  },

  origin: {
    key: 'origin',
    label: 'Origin',
    icon: '⤴',
    // No option exists until a file in the case has been cut. "Uploaded as a file" only
    // means something next to cut-out documents, so the Files unit offers just "Holds
    // documents inside".
    getValues: (docs) => {
      const counts = { cut: 0, uploaded: 0, holds: 0 };
      for (const d of docs) {
        if (isCutChild(d)) counts.cut++;
        else if (d.is_compilation_container) counts.holds++;
        else counts.uploaded++;
      }
      if (!counts.cut) counts.uploaded = 0;
      return ORIGIN_OPTIONS
        .filter(o => counts[o.value] > 0)
        .map(o => ({ value: o.value, label: o.label, count: counts[o.value] }));
    },
    matches: (d, v) => {
      if (v === 'cut') return isCutChild(d);
      if (v === 'holds') return !!d.is_compilation_container;
      if (v === 'uploaded') return !isCutChild(d) && !d.is_compilation_container;
      return false;
    },
    formatValue: (v) => findOption(ORIGIN_OPTIONS, v)?.label || v,
  },

  sourceFile: {
    key: 'sourceFile',
    label: 'Source file',
    icon: '🗂️',
    // One value per cut file; the count is the documents cut out of it.
    getValues: (docs) => {
      const counts = {};
      for (const d of docs) {
        if (isCutChild(d)) counts[d.container_id] = (counts[d.container_id] || 0) + 1;
      }
      return Object.entries(counts)
        .map(([value, count]) => ({ value, label: sourceFileName(value), count }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
    },
    matches: (d, v) => isCutChild(d) && d.container_id === v,
    formatValue: (v) => sourceFileName(v),
  },

  status: {
    key: 'status',
    label: 'Status',
    icon: '⚙️',
    getValues: (docs) => {
      const counts = {};
      for (const d of docs) {
        const s = (d.status || '').toLowerCase();
        if (s) counts[s] = (counts[s] || 0) + 1;
      }
      return STATUS_OPTIONS
        .filter(o => counts[o.value])
        .map(o => ({ value: o.value, label: o.label, count: counts[o.value] }));
    },
    matches: (d, v) => (d.status || '').toLowerCase() === String(v).toLowerCase(),
    formatValue: (v) => findOption(STATUS_OPTIONS, v)?.label || v,
  },

  labelStatus: {
    key: 'labelStatus',
    label: 'Label Status',
    icon: '🔖',
    getValues: (docs) => {
      let auto = 0, corrected = 0, unclassified = 0;
      for (const d of docs) {
        if (d.classification_source === 'human_manual') {
          corrected++;
        } else if (!d.document_purpose || d.document_purpose === 'unknown') {
          unclassified++;
        } else {
          auto++;
        }
      }
      return LABEL_STATUS_OPTIONS.map(o => {
        const count = o.value === 'auto_classified' ? auto
          : o.value === 'corrected' ? corrected
          : unclassified;
        return { value: o.value, label: o.label, count };
      }).filter(o => o.count > 0);
    },
    matches: (d, v) => {
      if (v === 'auto_classified') {
        return d.document_purpose && d.document_purpose !== 'unknown' && d.classification_source !== 'human_manual';
      }
      if (v === 'corrected') return d.classification_source === 'human_manual';
      if (v === 'unclassified') return !d.document_purpose || d.document_purpose === 'unknown';
      return false;
    },
    formatValue: (v) => findOption(LABEL_STATUS_OPTIONS, v)?.label || v,
  },

  tier: {
    key: 'tier',
    label: 'Search Tier',
    icon: '🔍',
    getValues: (docs) => {
      const counts = { skim: 0, surface: 0, deep: 0, indexing: 0 };
      for (const d of docs) {
        if (d.index_status === 'indexing' || d.index_status === 'pending') {
          counts.indexing++;
        }
        const t = d.tier || 'skim';
        if (counts[t] !== undefined) counts[t]++;
      }
      return TIER_OPTIONS
        .filter(o => counts[o.value] > 0)
        .map(o => ({ value: o.value, label: o.label, count: counts[o.value] }));
    },
    matches: (d, v) => {
      if (v === 'indexing') return d.index_status === 'indexing' || d.index_status === 'pending';
      return (d.tier || 'skim') === v;
    },
    formatValue: (v) => findOption(TIER_OPTIONS, v)?.label || v,
  },

  checklist: {
    key: 'checklist',
    label: 'Checklist',
    icon: '✓',
    visibleWhen: (ctx) => Boolean(ctx?.hasChecklist),
    getValues: (docs, ctx) => {
      if (!ctx?.checklistDocMap) return [];
      const matchedIds = new Set(Object.values(ctx.checklistDocMap).flat());
      let matched = 0, notMatched = 0;
      for (const d of docs) {
        if (matchedIds.has(d.id)) matched++;
        else notMatched++;
      }
      return CHECKLIST_OPTIONS.map(o => ({
        value: o.value,
        label: o.label,
        count: o.value === 'matched' ? matched : notMatched,
      })).filter(o => o.count > 0);
    },
    matches: (d, v, ctx) => {
      if (!ctx?.checklistDocMap) return true;
      const matchedIds = new Set(Object.values(ctx.checklistDocMap).flat());
      if (v === 'matched') return matchedIds.has(d.id);
      if (v === 'not_matched') return !matchedIds.has(d.id);
      return true;
    },
    formatValue: (v) => findOption(CHECKLIST_OPTIONS, v)?.label || v,
  },
  redaction: {
    // Key kept as 'redaction' so saved views / ?f= links from before excerpts
    // still resolve; the axis now covers every derived kind.
    key: 'redaction',
    label: 'Original or copy',
    icon: '🖍️',
    // Only surface the axis once at least one derived document (redacted
    // copy or excerpt) exists — keeps the picker clean otherwise.
    visibleWhen: (ctx) => Boolean(ctx?.hasRedactedDocs || ctx?.hasDerivedDocs),
    getValues: (docs) => {
      let redacted = 0, excerpt = 0, original = 0;
      for (const d of docs) {
        if (d.derived_kind === 'redaction' || (d.is_redacted && !d.derived_kind)) redacted++;
        else if (d.derived_kind === 'excerpt') excerpt++;
        else original++;
      }
      const opts = [];
      if (redacted) opts.push({ value: 'redacted', label: '🖍️ Redacted copy', count: redacted });
      if (excerpt) opts.push({ value: 'excerpt', label: '✂ Excerpt', count: excerpt });
      if (original) opts.push({ value: 'original', label: 'Original', count: original });
      return opts;
    },
    matches: (d, v) => {
      if (v === 'redacted') return d.derived_kind === 'redaction' || (!!d.is_redacted && !d.derived_kind);
      if (v === 'excerpt') return d.derived_kind === 'excerpt';
      return !d.derived_kind && !d.is_redacted;
    },
    formatValue: (v) => (v === 'redacted' ? 'Redacted copy' : v === 'excerpt' ? 'Excerpt' : 'Original'),
  },
  notes: {
    label: 'Notes',
    // Per-document notepad clip counts come from notesIndex (published by
    // InventoryTab from the loaded clips). Options only appear when notes exist.
    getValues: (docs) => {
      let has = 0, mine = 0;
      for (const d of docs) {
        if (docNoteCount(d.id) > 0) has++;
        if (docMineNoteCount(d.id) > 0) mine++;
      }
      const opts = [];
      if (has) opts.push({ value: 'has', label: '🗒️ Has notes', count: has });
      if (mine) opts.push({ value: 'mine', label: '🗒️ Noted by me', count: mine });
      return opts;
    },
    matches: (d, v) =>
      v === 'has' ? docNoteCount(d.id) > 0
        : v === 'mine' ? docMineNoteCount(d.id) > 0
          : true,
    formatValue: (v) => (v === 'mine' ? 'Noted by me' : 'Has notes'),
  },
};

/** Stable axis order for the picker. */
export const AXIS_ORDER = ['purpose', 'type', 'origin', 'sourceFile', 'status', 'labelStatus', 'tier', 'checklist', 'redaction', 'notes'];

/** Apply the active filters[] array against a doc list.
 *  Semantics: AND across axes, OR within an axis (multi-value).
 */
export function applyAxisFilters(docs, filters, ctx = {}) {
  if (!filters || filters.length === 0) return docs;

  // Group chips by axis — each axis becomes an array of allowed values.
  const byAxis = {};
  for (const c of filters) {
    if (!byAxis[c.axis]) byAxis[c.axis] = [];
    byAxis[c.axis].push(c.value);
  }

  let result = docs;
  for (const [axis, values] of Object.entries(byAxis)) {
    const def = FILTER_AXES[axis];
    if (!def) continue;
    // OR-within-axis: doc matches if it matches ANY of the values on this axis.
    result = result.filter(d => values.some(v => def.matches(d, v, ctx)));
  }
  return result;
}

/** Pretty-print a chip: "Purpose: Motion". */
export function formatChipLabel(filter, ctx = {}) {
  const def = FILTER_AXES[filter.axis];
  if (!def) return `${filter.axis}: ${filter.value}`;
  const value = def.formatValue ? def.formatValue(filter.value, ctx) : filter.value;
  return `${def.label}: ${value}`;
}

/** Picker rows: flat list of {axis, value, label, count, group, axisLabel, axisIcon}. */
export function getPickerRows(docs, ctx = {}) {
  const rows = [];
  for (const key of AXIS_ORDER) {
    const def = FILTER_AXES[key];
    if (!def) continue;
    if (def.visibleWhen && !def.visibleWhen(ctx)) continue;
    const values = def.getValues(docs, ctx) || [];
    for (const v of values) {
      rows.push({
        axis: key,
        axisLabel: def.label,
        axisIcon: def.icon,
        value: v.value,
        label: v.label,
        count: v.count,
        group: v.group,
      });
    }
  }
  return rows;
}

/** Contextual picker rows — counts reflect "what would happen if you added
 *  this filter," not raw case-wide counts.
 *
 *  Facet-exclusion algorithm: for each axis A, compute its rows against
 *  the doc set filtered by chips on OTHER axes (chips on A itself are
 *  excluded). This way:
 *    - axes with no active chips → counts vs. all currently-visible docs
 *    - axes with active chips → counts vs. "this axis's chips removed,"
 *      so other values on the same axis aren't artificially zeroed
 *
 *  facetDocs should be the doc set after folder/search/dupes filters but
 *  BEFORE axis-chip filters — see useInventoryData.
 */
export function getContextualPickerRows(facetDocs, activeFilters, ctx = {}) {
  const rows = [];
  for (const axisKey of AXIS_ORDER) {
    const def = FILTER_AXES[axisKey];
    if (!def) continue;
    if (def.visibleWhen && !def.visibleWhen(ctx)) continue;

    // Enumerate all possible values from the full facetDocs (so zero-count
    // values still appear in the picker, greyed but clickable — teaches
    // users which values exist).
    const possibleValues = def.getValues(facetDocs, ctx) || [];

    // Subset for counting: chips on OTHER axes applied.
    const otherChips = (activeFilters || []).filter(f => f.axis !== axisKey);
    const subset = applyAxisFilters(facetDocs, otherChips, ctx);

    for (const v of possibleValues) {
      const count = subset.reduce(
        (n, d) => n + (def.matches(d, v.value, ctx) ? 1 : 0),
        0
      );
      rows.push({
        axis: axisKey,
        axisLabel: def.label,
        axisIcon: def.icon,
        value: v.value,
        label: v.label,
        count,
        group: v.group,
      });
    }
  }
  // Stable sort: by axis order (already implicit), then within axis by count desc.
  return rows;
}
